import React, { useEffect, useMemo, useState } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { BBox, BoxAction, PogBBox } from '../types';
import { adjustToSize, Size2d } from '../utils/geometry';
import { BoxedImage, EmbeddingCountBadgeVariant, ImageBox } from './BoxedImage';
import { DragOverlay } from './DragOverlay';

const MAGNIFIER_ZOOM_LEVEL = 1;

interface ImageViewerProps<T extends BBox | PogBBox> {
  bboxes: T[];
  image?: HTMLImageElement;
  imageBase64?: string;
  onBoxClick: (bbox: T) => void;
  boxActions: BoxAction[];
  productRecognitionDetections?: ImageBox[];
  onProductRecognitionClick?: (detection: ImageBox, detectionIndex: number) => void;
  onBoxRightClick?: (detection: PogBBox | ImageBox) => void;
  planogramComplianceActions?: ImageBox[];
  onPlanogramComplianceClick?: (action: ImageBox) => void;
  enableZoom?: boolean; // Optional prop to enable/disable zoom functionality
  showProductImages?: boolean; // Optional prop to show product images on boxes
  showFallbackImages?: boolean; // Optional prop to show fallback images on boxes
  width?: number;
  height?: number;
  maxDisplayHeight?: number;
  autoSize?: boolean;
  fitToContainerWidth?: boolean;
  onBoxesSelected?: (selectedBboxes: T[]) => void;
  // Edit mode props
  editMode?: boolean;
  selectedDetection?: ImageBox | null;
  onDetectionEdited?: (updatedDetection: ImageBox) => void;
  onPersistDetectionEdit?: (detection: ImageBox) => void;
  onCornerDragStart?: (event: React.MouseEvent | React.TouchEvent, cornerIndex: number) => void;
  onMouseUpWhileDragging?: () => void;
  onBoxClear?: () => void;
  onNewShapeCreated?: (rect: [number, number, number, number]) => void;
  // DragOverlay props
  imageSrc?: string;
  imageDimensions?: { width: number; height: number };
  showShelfColumnLabels?: boolean;
  showEmbeddingsCount?: boolean;
  embeddingCountBadgeVariant?: EmbeddingCountBadgeVariant;
  imageBackgroundColor?: string;
  showFrameBorder?: boolean;
  imageContentVerticalAlign?: 'center' | 'flex-start';
  resetTransformOnSizeChange?: boolean;
  transformResetKey?: string | number;
}

export const ImageViewer = <T extends BBox | PogBBox>({
  bboxes,
  image,
  imageBase64,
  boxActions,
  onBoxClick,
  productRecognitionDetections = [],
  onProductRecognitionClick,
  onBoxRightClick,
  planogramComplianceActions = [],
  onPlanogramComplianceClick,
  enableZoom = true,
  showProductImages = false,
  showFallbackImages = true,
  width,
  height = 500, // Default height if not provided
  maxDisplayHeight,
  autoSize = true,
  fitToContainerWidth = false,
  onBoxesSelected,
  editMode = false,
  selectedDetection = null,
  onDetectionEdited,
  onPersistDetectionEdit,
  onMouseUpWhileDragging,
  onBoxClear,
  onNewShapeCreated,
  imageSrc,
  imageDimensions,
  showShelfColumnLabels = false,
  showEmbeddingsCount = false,
  embeddingCountBadgeVariant = 'default',
  imageBackgroundColor = '#f9fafb',
  showFrameBorder = true,
  imageContentVerticalAlign = 'center',
  resetTransformOnSizeChange = false,
  transformResetKey,
}: ImageViewerProps<T>) => {
  const [displayImage, setDisplayImage] = useState<HTMLImageElement | null>(image || null);
  const [size, setSize] = useState<Size2d>({ width: 0, height: 0 });
  const [magnifierPosition, setMagnifierPosition] = useState<{
    x: number;
    y: number;
    clientX: number;
  } | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    if (!fitToContainerWidth) return undefined;
    const node = containerRef.current;
    if (!node) return undefined;

    const updateContainerWidth = () => {
      const nextWidth = Math.floor(node.clientWidth || node.getBoundingClientRect().width);
      setContainerWidth(nextWidth > 0 ? nextWidth : undefined);
    };

    updateContainerWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateContainerWidth);
      return () => window.removeEventListener('resize', updateContainerWidth);
    }

    const observer = new ResizeObserver(updateContainerWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, [displayImage, fitToContainerWidth]);

  const boundedDisplayHeight =
    maxDisplayHeight && maxDisplayHeight > 0 ? Math.floor(maxDisplayHeight) : undefined;
  const targetWidth = fitToContainerWidth ? containerWidth || width : width;
  const targetHeight =
    fitToContainerWidth && targetWidth
      ? boundedDisplayHeight
      : boundedDisplayHeight
        ? Math.min(height, boundedDisplayHeight)
        : height;

  // Convert base64 to HTMLImageElement if provided
  useEffect(() => {
    if (imageBase64) {
      const img = new Image();
      img.onload = () => {
        setDisplayImage(img);
        setSize(
          adjustToSize(
            { width: img.naturalWidth, height: img.naturalHeight },
            { width: targetWidth, height: targetHeight }
          )
        );
      };
      img.src = imageBase64;
    } else if (image) {
      setDisplayImage(image);
      setSize(
        adjustToSize(
          { width: image.naturalWidth, height: image.naturalHeight },
          { width: targetWidth, height: targetHeight }
        )
      );
    }
  }, [image, imageBase64, targetHeight, targetWidth]);
  const boxActionByBboxId = useMemo(() => {
    const actionMap: Record<number, BoxAction | undefined> = {};
    boxActions.forEach((action) => {
      actionMap[action.bboxId] = action;
    });
    return actionMap;
  }, [boxActions]);

  const imageBoxes: ImageBox[] = useMemo(() => {
    const triggerBoxes = bboxes.map((bbox) => {
      const boxAction = boxActionByBboxId[bbox.bboxId];
      const boxActionIndex = boxAction ? boxActions.indexOf(boxAction) : -1;

      let shelfColumnLabel: string | undefined = undefined;

      if (showShelfColumnLabels) {
        if ('shelfColumnLabel' in bbox && bbox.shelfColumnLabel) {
          shelfColumnLabel = bbox.shelfColumnLabel;
        }
      }

      const isUnstructured = 'isUnstructured' in bbox ? Boolean(bbox.isUnstructured) : false;

      return {
        embeddingCropCount: bbox.embeddingCropCount,
        shape: [
          Math.min(bbox.x1, bbox.x2),
          Math.min(bbox.y1, bbox.y2),
          Math.abs(bbox.x2 - bbox.x1),
          Math.abs(bbox.y2 - bbox.y1),
        ] as [number, number, number, number],
        color: bbox.color || (boxAction ? 'rgba(34, 197, 94)' : 'rgba(59, 130, 246)'),
        strokeWidth: 10,
        badge: boxAction ? (boxActionIndex + 1).toString() : '',
        image:
          (showProductImages || (showFallbackImages && bbox.hasFallbackImage)) && bbox.imageUri
            ? bbox.imageUri
            : undefined,
        shelfColumnLabel,
        isUnstructured,
      };
    });

    // Add all overlays
    return [...triggerBoxes, ...productRecognitionDetections, ...planogramComplianceActions];
  }, [
    bboxes,
    boxActionByBboxId,
    boxActions,
    productRecognitionDetections,
    planogramComplianceActions,
    showProductImages,
    showFallbackImages,
    showShelfColumnLabels,
  ]);

  const resolvedTransformResetKey = [
    displayImage?.src ?? 'no-image',
    resetTransformOnSizeChange ? `${Math.round(size.width)}x${Math.round(size.height)}` : '',
    transformResetKey ?? '',
  ].join(':');

  useEffect(() => {
    if (!resetTransformOnSizeChange && transformResetKey === undefined) return;
    setMagnifierPosition(null);
  }, [resetTransformOnSizeChange, transformResetKey, resolvedTransformResetKey]);

  if (!displayImage) {
    return null;
  }

  const boxedImageComponent = (
    <BoxedImage
      key={displayImage.src}
      image={displayImage}
      boxes={imageBoxes}
      onBoxesSelected={
        onBoxesSelected
          ? (selectedIndices: number[]) => {
              // Convert indices back to actual bbox objects
              const selectedBboxes = selectedIndices.map((index) => bboxes[index]).filter(Boolean);
              onBoxesSelected(selectedBboxes);
            }
          : undefined
      }
      onBoxClick={(index) => {
        if (index < bboxes.length) {
          onBoxClick(bboxes[index]);
        } else if (index < bboxes.length + productRecognitionDetections.length) {
          const detectionIndex = index - bboxes.length;
          if (onProductRecognitionClick && productRecognitionDetections[detectionIndex]) {
            onProductRecognitionClick(productRecognitionDetections[detectionIndex], detectionIndex);
          }
        } else {
          const complianceIndex = index - bboxes.length - productRecognitionDetections.length;
          if (onPlanogramComplianceClick && planogramComplianceActions[complianceIndex]) {
            onPlanogramComplianceClick(planogramComplianceActions[complianceIndex]);
          }
        }
      }}
      onBoxRightClick={
        onBoxRightClick &&
        !(
          selectedDetection?.isPolygon &&
          selectedDetection?.points &&
          selectedDetection.points.length > 4
        )
          ? (index) => {
              if (productRecognitionDetections.length > 0) {
                if (index < bboxes.length + productRecognitionDetections.length) {
                  const detectionIndex = index - bboxes.length;
                  if (productRecognitionDetections[detectionIndex]) {
                    onBoxRightClick(productRecognitionDetections[detectionIndex] as ImageBox);
                  }
                }
              } else if (index < bboxes.length) {
                onBoxRightClick(bboxes[index] as PogBBox);
              }
            }
          : undefined
      }
      onNewShapeCreated={editMode ? onNewShapeCreated : undefined}
      showEmbeddingsCount={showEmbeddingsCount}
      embeddingCountBadgeVariant={embeddingCountBadgeVariant}
      styles={{
        backgroundColor: imageBackgroundColor,
        maxHeight: '100%',
        maxWidth: '100%',
        width: autoSize ? `${size.width}px` : 'auto',
        height: autoSize ? `${size.height}px` : 'auto',
      }}
    />
  );

  if (!enableZoom) {
    return (
      <div
        ref={containerRef}
        className={`relative block${showFrameBorder ? ' rounded-lg overflow-hidden border' : ''}`}
        style={{ backgroundColor: imageBackgroundColor }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: imageContentVerticalAlign,
            justifyContent: 'center',
          }}
        >
          <div style={{ position: 'relative' }}>{boxedImageComponent}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`relative block rounded-lg overflow-hidden${showFrameBorder ? ' border' : ''}`}
      style={{ backgroundColor: imageBackgroundColor }}
    >
      <TransformWrapper
        key={resolvedTransformResetKey}
        // initialScale={1}
        // minScale={0.1}
        // maxScale={10}
        centerOnInit={true}
        centerZoomedOut={true}
        wheel={{ step: 0.1 }}
        pinch={{ step: 5 }}
        doubleClick={{ mode: 'reset' }}
        limitToBounds={true}
        smooth={true}
        panning={{ disabled: false }}
        velocityAnimation={{
          sensitivity: 1,
          animationTime: 300,
          animationType: 'easeOut',
        }}
        alignmentAnimation={{
          sizeX: 100,
          sizeY: 100,
          velocityAlignmentTime: 300,
          animationTime: 300,
          animationType: 'easeOut',
        }}
      >
        {() => (
          <TransformComponent
            wrapperStyle={{
              width: '100%',
              height: `100%`,
            }}
            contentStyle={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: imageContentVerticalAlign,
              justifyContent: 'center',
            }}
          >
            <div style={{ position: 'relative' }}>
              {boxedImageComponent}
              {/* Render DragOverlay for selected detection in edit mode */}
              {editMode && selectedDetection && imageDimensions && imageSrc && onBoxClear && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                  }}
                >
                  <DragOverlay
                    box={selectedDetection}
                    imageWidth={imageDimensions.width}
                    imageHeight={imageDimensions.height}
                    imageSrc={imageSrc}
                    onDetectionEdited={onDetectionEdited}
                    onPersistDetectionEdit={onPersistDetectionEdit}
                    onMouseUpWhileDragging={onMouseUpWhileDragging}
                    onBoxClear={onBoxClear}
                    onMagnifierPositionChange={setMagnifierPosition}
                    styles={{
                      maxHeight: '100%',
                      maxWidth: '100%',
                      width: autoSize ? `${size.width}px` : 'auto',
                      height: autoSize ? `${size.height}px` : 'auto',
                    }}
                  />
                </div>
              )}
            </div>
          </TransformComponent>
        )}
      </TransformWrapper>

      {/* Magnifying glass - rendered outside TransformComponent to avoid zoom/pan effects */}
      {magnifierPosition && imageSrc && imageDimensions && containerRef.current && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            [(() => {
              const containerRect = containerRef.current!.getBoundingClientRect();
              const containerCenter = containerRect.left + containerRect.width / 2;
              return magnifierPosition.clientX > containerCenter ? 'left' : 'right';
            })()]: 0,
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            overflow: 'hidden',
            border: '3px solid white',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
            pointerEvents: 'none',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              width: '150px',
              height: '150px',
              backgroundImage: `url(${imageSrc})`,
              backgroundSize: `${
                imageDimensions.width * MAGNIFIER_ZOOM_LEVEL
              }px ${imageDimensions.height * MAGNIFIER_ZOOM_LEVEL}px`,
              backgroundPosition: `${
                -magnifierPosition.x * MAGNIFIER_ZOOM_LEVEL + 75
              }px ${-magnifierPosition.y * MAGNIFIER_ZOOM_LEVEL + 75}px`,
              backgroundRepeat: 'no-repeat',
            }}
          />
          {/* Red crosshair to show center point */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: 0,
              width: '100%',
              height: '1px',
              backgroundColor: 'red',
              transform: 'translateY(-50%)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: 0,
              width: '1px',
              height: '100%',
              backgroundColor: 'red',
              transform: 'translateX(-50%)',
            }}
          />
        </div>
      )}
    </div>
  );
};
